import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Section, SectionHeading, FadeIn, Stagger, StaggerItem } from "@/components/Section";
import {
  Search,
  Globe2,
  Repeat2,
  Building2,
  ClipboardList,
  HeartHandshake,
  ArrowRight,
  X,
  Check,
  Lock,
  Sparkles,
} from "lucide-react";

const DTC_BEHAVIORS = [
  "Searching online for solutions",
  "Engaging with digital-first healthcare platforms",
  "Purchasing cash-pay services directly",
  "Subscribing to ongoing wellness and treatment programs",
];

const DTC_TRAITS = [
  "Highly accessible",
  "Optimized for conversion",
  "Designed around convenience and speed",
  "Built to capture recurring consumer spend",
];

const DTC_MODEL = [
  "Consumer-first acquisition funnels",
  "Frictionless onboarding",
  "Clear cash-pay pricing",
  "Subscription-based programs",
  "Automated engagement",
  "Recurring revenue focus",
];

const TRADITIONAL_CLINIC = [
  "Limited program offerings",
  "Manual workflows",
  "Inconsistent follow-up",
  "No subscription model",
  "Lost revenue opportunities",
  "Reactive patient engagement",
];

const PLATFORM_ENABLES = [
  "Deploy the same types of high-demand cash-pay programs consumers are already purchasing",
  "Package services into clear, compelling offerings",
  "Streamline onboarding and engagement",
  "Support recurring revenue models",
  "Coordinate vendor-backed services through operational workflows",
];

const PRESERVED = [
  "Clinical independence",
  "Patient relationships",
  "Pricing control",
  "Operational ownership",
];

const DEMAND_SIGNALS = [
  "Weight management programs",
  "Sleep solutions",
  "Diagnostic testing",
  "Wellness optimization",
  "Longevity services",
  "More services coming soon",
];

export default function DtcMarketShift() {
  return (
    <div data-testid="dtc-market-shift">
      {/* === Section A: Already buying === */}
      <Section className="bg-white" testId="dtc-already-buying">
        <SectionHeading
          eyebrow="Market shift"
          title={
            <>
              Your patients are already buying cash-pay healthcare —{" "}
              <span className="text-blue-600">just not always from you.</span>
            </>
          }
          intro="Consumers are increasingly accessing healthcare services directly through digital-first platforms, often outside of their primary clinic relationships."
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-7 h-full">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                What modern healthcare consumers are doing
              </div>
              <ul className="mt-5 space-y-3">
                {DTC_BEHAVIORS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.08} className="lg:col-span-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                What direct-to-consumer platforms are
              </div>
              <ul className="mt-5 space-y-3">
                {DTC_TRAITS.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-md bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                      <Sparkles className="w-3 h-3" />
                    </span>
                    <span className="text-sm text-slate-700">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>

        {/* Flow diagrams */}
        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <FlowDiagram
            label="Consumer path today"
            tone="muted"
            steps={[
              { icon: HeartHandshake, label: "Patient" },
              { icon: Search, label: "Online search" },
              { icon: Globe2, label: "DTC platform" },
              { icon: Repeat2, label: "Subscription program" },
            ]}
          />
          <FlowDiagram
            label="Consumer path with infrastructure"
            tone="brand"
            steps={[
              { icon: HeartHandshake, label: "Patient" },
              { icon: Building2, label: "Clinic + platform" },
              { icon: ClipboardList, label: "Program enrollment" },
              { icon: Repeat2, label: "Ongoing care + revenue retention" },
            ]}
          />
        </div>

        {/* Insight callout */}
        <FadeIn delay={0.15}>
          <div className="mt-14 relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 lg:p-12">
            <span className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-9">
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-300 mb-3">
                  Key insight
                </div>
                <p className="font-display text-2xl sm:text-3xl font-bold leading-tight">
                  The shift isn't coming — it's already happening. The question is whether your clinic
                  participates in this demand, or loses visibility into it.
                </p>
              </div>
              <div className="lg:col-span-3 lg:flex lg:justify-end">
                <Link
                  to="/demo"
                  data-testid="dtc-insight-cta"
                  className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* === Section B: Competing for the same patient === */}
      <Section className="bg-slate-50 border-y border-slate-200" testId="dtc-comparison">
        <SectionHeading
          eyebrow="Same patient · different infrastructure"
          title="Competing for the same patient — different infrastructure."
          intro="Both models serve the same consumer. The difference is operational, not clinical."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <FadeIn>
            <ComparisonCard
              testId="dtc-model-card"
              eyebrow="DTC model"
              title="Built for direct consumer capture"
              tone="dark"
              items={DTC_MODEL}
              icon={Sparkles}
            />
          </FadeIn>
          <FadeIn delay={0.08}>
            <ComparisonCard
              testId="dtc-clinic-card"
              eyebrow="Clinic without infrastructure"
              title="Built for episodic visits, not programs"
              tone="muted"
              items={TRADITIONAL_CLINIC}
              icon={X}
              negative
            />
          </FadeIn>
        </div>

        <FadeIn delay={0.15}>
          <div className="mt-12 rounded-2xl border border-blue-100 bg-blue-50/60 p-6 lg:p-8 text-center">
            <p className="font-display text-lg sm:text-xl font-semibold text-slate-900 leading-snug max-w-3xl mx-auto">
              Clinics are not losing patients due to lack of clinical expertise — they are losing them due to lack of infrastructure.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* === Section C: Bring consumer back === */}
      <Section className="bg-white" testId="dtc-bring-back">
        <SectionHeading
          eyebrow="Infrastructure response"
          title="Bring the consumer back into your ecosystem."
          intro="Purpose-built to help clinics meet patients where they are — without becoming a telehealth company."
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <FadeIn className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-7 h-full">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                What the platform enables
              </div>
              <ul className="mt-5 space-y-3.5">
                {PLATFORM_ENABLES.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.08} className="lg:col-span-5">
            <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-7 h-full">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                <Lock className="w-3.5 h-3.5" /> What stays with the clinic
              </div>
              <ul className="mt-5 space-y-3.5">
                {PRESERVED.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-md bg-white/10 border border-white/15 text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm text-slate-100">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-400 leading-relaxed">
                Clinical decisions remain with the clinic. Nodera Health provides operational infrastructure only.
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* === Section D: Capture demand === */}
      <Section className="bg-slate-50 border-y border-slate-200" testId="dtc-capture-demand">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Existing demand"
              title="Capture demand that already exists."
              intro="You don't need to create demand. Consumers are already seeking these categories — the challenge is operational."
            />
          </div>

          <div className="lg:col-span-7">
            <Stagger stagger={0.05}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DEMAND_SIGNALS.map((d, i) => {
                  const upcoming = /coming soon/i.test(d);
                  return (
                    <StaggerItem key={i}>
                      <div
                        data-testid={`dtc-demand-${i}`}
                        className={`rounded-xl border p-4 flex items-center gap-3 ${
                          upcoming
                            ? "border-dashed border-blue-300 bg-blue-50/40"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <span
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            upcoming ? "bg-white text-blue-600 border border-blue-200" : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </span>
                        <span className={`text-sm font-semibold ${upcoming ? "text-blue-700" : "text-slate-800"}`}>
                          {d}
                        </span>
                      </div>
                    </StaggerItem>
                  );
                })}
              </div>
            </Stagger>

            <FadeIn delay={0.15}>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                  The operational challenge
                </div>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
                  {[
                    "Delivering these programs efficiently",
                    "Packaging them clearly",
                    "Operating them consistently",
                    "Retaining the patient relationship",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-900" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>

        <FadeIn delay={0.2}>
          <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
            <p className="font-display text-base sm:text-lg text-slate-700 leading-relaxed text-center max-w-4xl mx-auto">
              Our platform enables clinics to <span className="font-semibold text-slate-900">participate in the modern cash-pay healthcare economy</span> — without becoming a telehealth company, and without compromising clinical control.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* === Section E: Final CTA for this block === */}
      <Section className="bg-white" testId="dtc-final-cta">
        <FadeIn>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white p-10 lg:p-14">
            <span className="absolute -top-40 -left-20 w-[460px] h-[460px] rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-300 mb-3">
                  Meet patients where they are
                </div>
                <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                  Keep your patients — and the programs they're already seeking.
                </h3>
                <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">
                  See how clinics are deploying modern cash-pay programs that align with today's consumer behavior — while retaining clinical independence and patient relationships.
                </p>
              </div>
              <div className="lg:col-span-4 lg:flex lg:justify-end">
                <Link
                  to="/demo"
                  data-testid="dtc-final-cta-btn"
                  className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  Book a Platform Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
}

function FlowDiagram({ label, tone, steps }) {
  const isBrand = tone === "brand";
  const lineCls = isBrand ? "bg-blue-300/70" : "bg-slate-300";
  // Equal-width columns + connector centered at icon midpoint (top-6 = half
  // of w-12/h-12 icon = 24px). `left-[12.5%]` / `right-[12.5%]` work for
  // any 4-step row because each column is 25% wide and the icon centers in
  // its column → first icon center at 12.5%, last at 87.5%.
  return (
    <FadeIn>
      <div
        className={`rounded-2xl border p-6 h-full ${
          isBrand ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-white"
        }`}
      >
        <div
          className={`text-[11px] uppercase tracking-[0.18em] font-semibold mb-6 ${
            isBrand ? "text-blue-700" : "text-slate-500"
          }`}
        >
          {label}
        </div>

        {/* Icon rail */}
        <div className="relative">
          <div
            aria-hidden
            className={`absolute top-6 left-[12.5%] right-[12.5%] h-px ${lineCls}`}
          />
          <div
            className="relative grid"
            style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
          >
            {steps.map((s, i) => (
              <motion.div
                key={`icon-${i}`}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex justify-center"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${
                    isBrand ? "bg-blue-600 text-white" : "bg-slate-900 text-white"
                  }`}
                >
                  <s.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Labels rail — top-aligned so 1-line and 2-line labels both anchor at the same y */}
        <div
          className="mt-3 grid items-start"
          style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
        >
          {steps.map((s, i) => (
            <div key={`label-${i}`} className="text-center px-1">
              <div className="text-[11px] font-semibold text-slate-800 leading-snug">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </FadeIn>
  );
}

function ComparisonCard({ testId, eyebrow, title, items, tone, icon: Icon, negative }) {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testId}
      className={`h-full rounded-2xl p-7 border ${
        dark ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${dark ? "text-blue-300" : "text-slate-500"}`}>
          {eyebrow}
        </div>
        <span
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
            dark ? "bg-white/10 border border-white/10" : "bg-slate-50 border border-slate-200"
          }`}
        >
          <Icon className={`w-4 h-4 ${dark ? "text-blue-300" : "text-slate-500"}`} />
        </span>
      </div>
      <h3 className={`mt-3 font-display text-xl font-semibold leading-snug ${dark ? "text-white" : "text-slate-900"}`}>
        {title}
      </h3>
      <ul className="mt-5 space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm">
            <span
              className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${
                negative
                  ? "bg-amber-50 text-amber-600 border border-amber-100"
                  : dark
                  ? "bg-blue-500/20 text-blue-300"
                  : "bg-blue-50 text-blue-600"
              }`}
            >
              {negative ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            </span>
            <span className={dark ? "text-slate-100" : "text-slate-700"}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
